import type { MutableRefObject } from 'react';

import type { GetItemPropsOptions } from 'downshift';

import { getSharedFooterProps } from './sharedFooterLogic';
import type {
  GetSelectedItemProps,
  FooterProps,
  GenericItem,
  GetItemProps,
} from '../types';

export const getMultipleSelectionHandlers = <TItem extends GenericItem>({
  id,
  selectedItems,
  setSelectedItems,
  isItemDisabled,
  closeMenu,
  rawGetItemProps,
  rawGetSelectedItemProps,
  refToFocusOnClear,
}: {
  id: string;
  selectedItems: TItem[];
  setSelectedItems: (items: TItem[]) => void;
  isItemDisabled: (item: TItem) => boolean;
  closeMenu: () => void;
  rawGetItemProps: GetItemProps<TItem>;
  rawGetSelectedItemProps: GetSelectedItemProps<TItem>;
  refToFocusOnClear: MutableRefObject<HTMLElement>;
}) => {
  const getSelectedItemProps: GetSelectedItemProps<TItem> = (options) => {
    const disabled = isItemDisabled(options.selectedItem);
    return rawGetSelectedItemProps({ disabled, ...options });
  };

  const reset = () => {
    const disabledSelectedItems = selectedItems.filter((item) =>
      isItemDisabled(item),
    );

    setSelectedItems(disabledSelectedItems);
  };

  const footerProps = getSharedFooterProps({
    id,
    reset,
    closeMenu,
    refToFocusOnClear,
  });

  const getFooterProps: () => FooterProps = () => ({
    selectedItems,
    ...footerProps,
  });

  const getItemProps = ({
    item,
    isSelected: isSelectedProp,
    ...itemProps
  }: GetItemPropsOptions<TItem>) => {
    const isSelected = isSelectedProp || selectedItems.includes(item);

    const toReturn = rawGetItemProps({ item, isSelected, ...itemProps });

    return {
      isMultiple: true,
      ...toReturn,
    };
  };

  return {
    controlProps: { getSelectedItemProps },
    menuProps: {
      getFooterProps,
      getItemProps,
    },
  };
};

export const toggleItemSelection = <TItem extends GenericItem>({
  item,
  selectedItems,
  addSelectedItem,
  removeSelectedItem,
}: {
  /** The item that was just clicked or chosen via keyboard */
  item: TItem | null | undefined;
  /** The currently selected items */
  selectedItems: TItem[];
  /** Function to add an item to the selected items */
  addSelectedItem: (item: TItem) => void;
  /** Function to remove an item from the selected items */
  removeSelectedItem: (item: TItem) => void;
}) => {
  if (!item) {
    return;
  }

  if (!selectedItems.includes(item)) {
    addSelectedItem(item);
  } else {
    removeSelectedItem(item);
  }
};
